import axios from 'axios';

export const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL;

const client = axios.create({
    baseURL: ADMIN_API_URL,
    withCredentials: true
});

export const products = {
    async list(searchParams) {
        const response = await client.get('/v2/products', { params: searchParams });

        return response.data;
    },
    async overlaySearch(searchParams) {
        const response = await client.get('/v2/products/overlay-search', { params: searchParams });

        return response.data;
    },
    async get(id, searchParams) {
        const response = await client.get(`/v2/products/${id}`, { params: searchParams });

        return response.data;
    },
    async update({ id, body }) {
        const response = await client.patch(`/v2/products/${id}`, body);

        return response.data;
    }
};

export const creatives = {
    async list(searchParams) {
        const response = await client.get('/v2/creatives', { params: searchParams });

        return response.data;
    },
    async get(id, searchParams) {
        const response = await client.get(`/v2/creatives/${id}`, { params: searchParams });

        return response.data;
    },
    async update({ id, body }) {
        const response = await client.patch(`/v2/creatives/${id}`, body);

        return response.data;
    }
};

export const ccpa = {
    async list(searchParams) {
        const response = await client.get('/v2/ccpa', { params: searchParams });

        return response.data;
    },
    async get(id, searchParams) {
        const response = await client.get(`/v2/ccpa/${id}`, { params: searchParams });

        return response.data;
    },
    async create(body) {
        const response = await client.post('/v2/ccpa', body);

        return response.data;
    },
    async update(id, body) {
        const response = await client.patch(`/v2/ccpa/${id}`, body);

        return response.data;
    },
    async remove(id) {
        const response = await client.delete(`/v2/ccpa/${id}`);

        return response.data;
    },
    async submit(ids) {
        const response = await client.post('/v2/ccpa/submit', ids);

        return response.data;
    }
};

export const accounts = {
    async update(id, body) {
        const response = await client.patch(`/v2/accounts/${id}`, body);

        return response.data;
    },
    async updateProductStatus(id, body) {
        const response = await client.post(`/v2/accounts/${id}/update-product-status`, body);

        return response.data;
    },
    async get(id) {
        const response = await client.get(`/v2/accounts/${id}`);

        return response.data;
    },
    async list(searchParams) {
        const response = await client.get('/v2/accounts', { params: searchParams });

        return response.data;
    },
    async getFeatureFlags(id) {
        const response = await client.get(`/v2/accounts/${id}/feature-flags`);

        return response.data;
    },
    async updateFeatureFlag(id, body) {
        const response = await client.patch(`/v2/accounts/${id}/feature-flags`, body);

        return response.data;
    },
    async reviewList(searchParams) {
        const response = await client.get('/v2/accounts/review', { params: searchParams });

        return response.data;
    },
    async stats(name = 'status') {
        const response = await client.get(`/v2/accounts/stats/${name}`);

        return response.data;
    },
    async users(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/users`);

        return response.data;
    },
    async removeUser(accountId, userId) {
        const response = await client.delete(`/v2/accounts/${accountId}/users/${userId}`);

        return response.data;
    },
    async terminateAccount(accountId) {
        const response = await client.delete(`/v2/accounts/${accountId}/terminate`);

        return response.data;
    },
    async loginAsUser(accountId, userId) {
        const response = await client.post(`/v2/accounts/${accountId}/auth/${userId}/login`);

        return response.data;
    },
    async info(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/info`);

        return response.data;
    },
    async settings(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/settings`);

        return response.data;
    },
    async health(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/health`);

        return response.data;
    },
    async blockedProductsList(accountId, searchParams) {
        const response = await client.get(`/v2/accounts/${accountId}/blocked-products`, { params: searchParams });

        return response.data;
    },
    async blockedProductsCreate(accountId, ecommerceId) {
        const response = await client.post(`/v2/accounts/${accountId}/blocked-products/${ecommerceId}`);

        return response.data;
    },
    async blockedProductsDelete(accountId, productId) {
        const response = await client.delete(`/v2/accounts/${accountId}/blocked-products/${productId}`);

        return response.data;
    },
    async paymentMethodGet(id) {
        const response = await client.get(`/v2/accounts/${id}/payment-method`);

        return response.data;
    },
    async paymentMethodDetach(id) {
        const response = await client.delete(`/v2/accounts/${id}/payment-method`);

        return response.data;
    },
    async invoices(accountId, searchParams) {
        const response = await client.get(`/v2/accounts/${accountId}/invoices`, { params: searchParams });

        return response.data;
    },
    async processInvoice(accountId, invoiceId) {
        const response = await client.post(`/v2/accounts/${accountId}/invoices/${invoiceId}/process`);

        return response.data;
    },
    async recalculatePerformance(accountId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/invoices/recalculate`, body);

        return response.data;
    },
    async refundInvoice(accountId, invoiceId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/invoices/${invoiceId}/refund`, body);

        return response.data;
    },
    async splitInvoice(invoiceId, accountId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/invoices/${invoiceId}/split`, body);

        return response.data;
    },
    async changeStatusInvoice(accountId, invoiceId, body) {
        const response = await client.patch(`/v2/accounts/${accountId}/invoices/${invoiceId}`, body);

        return response.data;
    },
    async payouts(accountId, searchParams) {
        const response = await client.get(`/v2/accounts/${accountId}/payouts`, { params: searchParams });

        return response.data;
    },
    async markAsPaid(payoutId, data) {
        const response = await client.post(`/v2/accounts/payout/mark-paid/${payoutId}`, data);

        return response.data;
    },
    async processPayouts(payoutId, accountId) {
        const response = await client.post(`/v2/accounts/${accountId}/payout/process/${payoutId}`);

        return response.data;
    },
    async restrictions(accountId, searchParams) {
        const response = await client.get(`/v2/accounts/${accountId}/restrictions`, { params: searchParams });

        return response.data;
    },
    async removeRestriction(accountId, recordId) {
        const response = await client.delete(`/v2/accounts/${accountId}/restrictions/${recordId}`);

        return response.data;
    },
    async bulkRemoveRestriction(accountId) {
        const response = await client.delete(`/v2/accounts/${accountId}/restrictions`);

        return response.data;
    },
    async patchRestriction(accountId, recordId, body) {
        const response = await client.patch(`/v2/accounts/${accountId}/restrictions/${recordId}`, body);

        return response.data;
    },
    async saveRestriction(accountId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/restrictions`, body);

        return response.data;
    },
    async uploadRestrictionCSV(accountId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/restrictions/upload`, body);

        return response.data;
    },
    async scriptCheck(accountId) {
        const response = await client.post(`/v2/accounts/${accountId}/script-check`);

        return response.data;
    },
    async widgetBlockedProductsList(accountId, searchParams) {
        const response = await client.get(`/v2/accounts/${accountId}/widget-blocked-products`, {
            params: searchParams
        });

        return response.data;
    },
    async widgetBlockedProductsPatch(accountId, productId, isBlocked) {
        const response = await client.patch(`/v2/accounts/${accountId}/widget-blocked-products/${productId}`, {
            isBlocked
        });

        return response.data;
    },
    async tripleWhaleSettingsPatch(accountId, body) {
        const response = await client.patch(`/v2/accounts/${accountId}/triple-whale-settings`, body);

        return response.data;
    },
    async tripleWhaleTokenTest(accountId, body) {
        const response = await client.post(`/v2/accounts/${accountId}/triple-whale-settings/test-token`, body);

        return response.data;
    },
    async ga4SettingsPatch(accountId, body) {
        const response = await client.patch(`/v2/accounts/${accountId}/ga4-settings`, body);

        return response.data;
    },
    async campaigns(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/campaigns`);

        return response.data;
    },
    async integrations(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/integrations`);

        return response.data;
    },
    async apiKeysList(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/api-keys`);

        return response.data;
    },
    async apiKeyGenerate(accountId) {
        const response = await client.post(`/v2/accounts/${accountId}/api-keys`);

        return response.data;
    },
    async apiKeyRevoke(accountId, keyId) {
        const response = await client.delete(`/v2/accounts/${accountId}/api-keys/${keyId}`);

        return response.data;
    }
};

export const orders = {
    async list(searchParams) {
        const response = await client.get('/v2/orders', { params: searchParams });

        return response.data;
    },
    async get(id) {
        const response = await client.get(`/v2/orders/${id}`);

        return response.data;
    },
    async details(id) {
        const response = await client.get(`/v2/orders/${id}/details`);

        return response.data;
    },
    async insights(id) {
        const response = await client.get(`/v2/orders/${id}/insights`);

        return response.data;
    },
    async analytics(id, page, klicklyOnly = true, brandOnly = false) {
        const response = await client.get(`/v2/orders/${id}/analytics`, {
            params: { page, klicklyOnly, brandOnly }
        });

        return response.data;
    },
    async analyticsV2(id, klicklyOnly = true, brandOnly = false) {
        const response = await client.get(`/v2/orders/${id}/analytics`, {
            params: { klicklyOnly, brandOnly }
        });

        return response.data;
    },
    async analyticsV2CSV(id, klicklyOnly = true, brandOnly = false) {
        const response = await client.get(`/v2/orders/${id}/analytics/csv`, {
            params: { klicklyOnly, brandOnly },
            responseType: 'blob'
        });

        return response.data;
    },
    async refund(id, values) {
        const response = await client.post(`/v2/orders/${id}/refund`, values);

        return response.data;
    }
};

export const review = {
    async accountList(searchParams) {
        const response = await client.get('/v2/review', { params: searchParams });

        return response.data;
    },

    async productList(accountId, searchParams) {
        const response = await client.get(`/v2/review/${accountId}/products`, { params: searchParams });

        return response.data;
    },

    async changeProductStatus(accountId, body) {
        const response = await client.post(`/v2/review/${accountId}/products`, body);

        return response.data;
    }
};

export const googleAds = {
    async get(accountId) {
        const response = await client.get(`/v2/google-ads/${accountId}`);

        return response.data;
    },
    async update(accountId, values) {
        const response = await client.post(`/v2/google-ads/${accountId}`, values);

        return response.data;
    }
};

export const scriptTags = {
    async list(accountId) {
        const response = await client.get(`/v2/accounts/${accountId}/script-tags`);

        return response.data;
    },
    async create(accountId, values) {
        const response = await client.post(`/v2/accounts/${accountId}/script-tags`, values);

        return response.data;
    },
    async patch(accountId, scriptTagId, values) {
        const response = await client.patch(`/v2/accounts/${accountId}/script-tags/${scriptTagId}`, values);

        return response.data;
    },
    async submitJob(accountId, scriptTagId, values) {
        const response = await client.post(`/v2/accounts/${accountId}/script-tags/${scriptTagId}/submit`, values);

        return response.data;
    }
};

export const customCreatives = {
    async accountsList(searchParams) {
        const response = await client.get('/v2/custom-creatives/accounts', { params: searchParams });

        return response.data;
    },
    async accountCampaignsList(accountId, searchParams) {
        const response = await client.get(`/v2/custom-creatives/${accountId}/campaigns`, { params: searchParams });

        return response.data;
    },
    async customCreativesGet(id, searchParams) {
        const response = await client.get(`/v2/custom-creatives/${id}`, { params: searchParams });

        return response.data;
    },
    async customCreativesUpdate({ id, body }) {
        const response = await client.post(`/v2/custom-creatives/${id}`, body);

        return response.data;
    }
};

export const dspCreatives = {
    async list(searchParams) {
        const response = await client.get('/v2/dsp/creatives', { params: searchParams });

        return response.data;
    },
    async create(body) {
        const response = await client.post('/v2/dsp/creatives', body);

        return response.data;
    }
};

export const awaCustomCreatives = {
    async getSettings(accountId) {
        const response = await client.get(`/v2/awa-custom-creatives/accounts/${accountId}/settings`);

        return response.data;
    },
    async createSettings(accountId, body) {
        const response = await client.post(`/v2/awa-custom-creatives/accounts/${accountId}/settings`, body);

        return response.data;
    },
    async list(accountId, searchParams) {
        const response = await client.get(`/v2/awa-custom-creatives/accounts/${accountId}/creatives`, {
            params: searchParams
        });

        return response.data;
    },
    async get(accountId, creativeId) {
        const response = await client.get(`/v2/awa-custom-creatives/accounts/${accountId}/creatives/${creativeId}`);

        return response.data;
    },
    async create(accountId, body) {
        const response = await client.post(`/v2/awa-custom-creatives/accounts/${accountId}/creatives`, body);

        return response.data;
    },
    async update(accountId, creativeId, body) {
        const response = await client.put(
            `/v2/awa-custom-creatives/accounts/${accountId}/creatives/${creativeId}`,
            body
        );

        return response.data;
    },
    async remove(accountId, creativeId) {
        const response = await client.delete(`/v2/awa-custom-creatives/accounts/${accountId}/creatives/${creativeId}`);

        return response.data;
    }
};

export const upliftStudies = {
    async list(searchParams) {
        const response = await client.get('/v2/uplift-studies', { params: searchParams });

        return response.data;
    },
    async get(id) {
        const response = await client.get(`/v2/uplift-studies/${id}`);

        return response.data;
    },
    async create(body) {
        const response = await client.post('/v2/uplift-studies', body);

        return response.data;
    },
    async update(id, body) {
        const response = await client.patch(`/v2/uplift-studies/${id}`, body);

        return response.data;
    },
    async remove(id) {
        const response = await client.delete(`/v2/uplift-studies/${id}`);

        return response.data;
    },
    async activate(id) {
        const response = await client.post(`/v2/uplift-studies/${id}/activate`);

        return response.data;
    },
    async pause(id) {
        const response = await client.post(`/v2/uplift-studies/${id}/pause`);

        return response.data;
    },
    async end(id) {
        const response = await client.post(`/v2/uplift-studies/${id}/end`);

        return response.data;
    },
    async startProcessing(id) {
        const response = await client.post(`/v2/uplift-studies/${id}/start-processing`);

        return response.data;
    }
};

export const awaSettings = {
    async getConfig() {
        const response = await client.get('/v2/awa-settings/config');

        return response.data;
    },

    async getStatus(accountId) {
        const response = await client.get(`/v2/awa-settings/accounts/${accountId}/status`);

        return response.data;
    },

    async toggleDefaultLayout(accountId, layoutId, enabled) {
        const response = await client.put(`/v2/awa-settings/accounts/${accountId}/layouts/${layoutId}/toggle`, {
            enabled
        });

        return response.data;
    },

    async toggleProportionalTraffic(accountId, enabled) {
        const response = await client.put(`/v2/awa-settings/accounts/${accountId}/proportional-traffic/toggle`, {
            enabled
        });

        return response.data;
    }
};

export const cpv2Orders = {
    async list(searchParams) {
        const response = await client.get('/v2/cpv2-orders', { params: searchParams });

        return response.data;
    },
    async metrics(params) {
        const response = await client.get('/v2/cpv2-orders/metrics', { params });

        return response.data;
    },
    async reconciliation(params) {
        const response = await client.get('/v2/cpv2-orders/reconciliation', { params });

        return response.data;
    },
    async detail(accountId, orderId) {
        const response = await client.get(`/v2/cpv2-orders/${accountId}/${orderId}`);

        return response.data;
    },
    async createAttempt({ accountId, orderId, strategy }) {
        const response = await client.post(`/v2/cpv2-orders/${accountId}/${orderId}/attempts`, { strategy });

        return response.data;
    },
    async bulkAttempt({ items, strategy }) {
        const response = await client.post('/v2/cpv2-orders/attempts/bulk', { items, strategy });

        return response.data;
    },
    async bulkAttemptFiltered({ searchParams, strategy }) {
        // Re-attribute EVERY order matching the current grid filter. Send the
        // filters (minus pagination) as the POST body so the backend targets
        // exactly what the grid shows; stripUnknown drops anything extra. No
        // per-request timeout override — the backend enqueues (fast) rather than
        // processing inline, and the created-date window bounds the set.
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('limit');
        const filters = Object.fromEntries(params.entries());
        const response = await client.post('/v2/cpv2-orders/attempts/bulk-filtered', { ...filters, strategy });

        return response.data;
    },
    async exportCsv(searchParams) {
        // Export covers the whole filtered set, not a page — strip pagination
        // (the export schema rejects unknown params). responseType blob so the
        // CSV body comes back intact for a browser download.
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('limit');
        const response = await client.get('/v2/cpv2-orders/export', { params, responseType: 'blob' });

        return response.data;
    }
};

export default client;
